import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gamepad2, Gift, Brain, Sparkles, ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { APP_UNIVERS, gardienById } from "@/data/gardiens";

// URL de l'application Passerelle
const APP_URL = "https://objetsdouceur.com";

const DEFAULTS = {
  jeux_eyebrow: "Espace Jeux & Accompagnement",
  jeux_title: "Passerelle, l'application qui stimule la mémoire en douceur",
  jeux_subtitle: "En complément de nos objets, nous proposons une application dédiée, Passerelle, pensée pour stimuler la mémoire de manière ludique. Des activités simples, chaleureuses et adaptées aux personnes touchées par Alzheimer, l'autisme ou une maladie dégénérative — et à leurs proches.",
  jeux_hero_quote: "Jouer, se souvenir, partager — chaque jour un peu plus sereinement.",
  jeux_hero_image: "/affiche-espace-jeux.jpg",
  jeux_offer_title: "Accès illimité offert dès 100 € d'achat",
  jeux_offer_text: "Dès 100 € d'achat sur ce site, l'accès illimité à l'application Passerelle vous est offert — une offre réservée aux particuliers. Un objet qui prend soin, et une application qui accompagne — ensemble.",
  jeux_offer_b1: "Aucun abonnement, aucun frais caché",
  jeux_offer_b2: "Offre réservée aux particuliers, dès 100 € d'achat",
  jeux_offer_b3: "Mises à jour et nouveaux jeux inclus",
  jeux_features_title: "Ce que propose l'application",
  jeux_feature_1_t: "Stimulation de la mémoire",
  jeux_feature_1_d: "Des jeux doux et progressifs conçus pour entretenir les souvenirs et les capacités cognitives, à son propre rythme.",
  jeux_feature_2_t: "Une approche ludique",
  jeux_feature_2_d: "Apprendre et se souvenir en s'amusant : associations d'images, sons familiers, petites énigmes bienveillantes.",
  jeux_feature_3_t: "Personnalisé & accompagnant",
  jeux_feature_3_d: "L'application s'adapte à chaque personne et accompagne les aidants dans les moments partagés.",
};

const FEATURE_ICONS = [Brain, Gamepad2, Sparkles];

const EspaceJeux = () => {
  const [count, setCount] = useState(null);
  const [c, setC] = useState(DEFAULTS);
  useEffect(() => { api.get("/products").then((r) => setCount(r.data.length)).catch(() => {}); }, []);
  useEffect(() => { api.get("/content").then((r) => setC({ ...DEFAULTS, ...r.data })).catch(() => {}); }, []);

  const features = FEATURE_ICONS.map((icon, i) => ({
    icon,
    t: c[`jeux_feature_${i + 1}_t`],
    d: c[`jeux_feature_${i + 1}_d`],
  }));
  const offerBullets = [c.jeux_offer_b1, c.jeux_offer_b2, c.jeux_offer_b3];

  return (
    <div className="pt-32">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.3em] text-primary mb-5">{c.jeux_eyebrow}</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl font-light leading-tight">{c.jeux_title}</h1>
            <p className="mt-6 text-lg text-foreground/70 max-w-2xl leading-relaxed">
              {c.jeux_subtitle}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <a href={APP_URL} target="_blank" rel="noopener noreferrer" data-testid="discover-app-btn"
              className="group inline-flex items-center gap-2 mt-9 rounded-full bg-primary text-primary-foreground px-9 py-4 hover:scale-105 transition-transform duration-300">
              Découvrir Passerelle <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <div className="relative rounded-3xl bg-secondary/20 border border-border/50 overflow-hidden grain">
              {c.jeux_hero_image ? (
                <>
                  <img src={c.jeux_hero_image} alt="Un moment de jeu et de lien avec l'application Passerelle"
                    data-testid="jeux-hero-image" className="w-full aspect-[4/3] object-cover" />
                  <p className="font-serif-display text-2xl sm:text-3xl p-8 leading-snug">{c.jeux_hero_quote}</p>
                </>
              ) : (
                <div className="p-10">
                  <Gamepad2 className="w-16 h-16 text-primary" strokeWidth={1.2} />
                  <p className="font-serif-display text-3xl mt-6 leading-snug">{c.jeux_hero_quote}</p>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* OFFRE GRATUITE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <Reveal>
          <div className="rounded-[2.5rem] bg-warm text-warm-foreground px-8 sm:px-14 py-16 grain overflow-hidden">
            <div className="flex flex-col lg:flex-row lg:items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <Gift className="w-8 h-8" strokeWidth={1.5} />
                  <span className="text-sm uppercase tracking-[0.3em]">Offert</span>
                </div>
                <h2 className="font-serif-display text-4xl sm:text-5xl font-light mt-5 leading-tight">{c.jeux_offer_title}</h2>
                <p className="mt-5 text-warm-foreground/90 max-w-2xl leading-relaxed">
                  {c.jeux_offer_text}
                </p>
                <ul className="mt-7 space-y-3">
                  {offerBullets.map((b, i) => (
                    <li key={i} className="flex items-center gap-3"><Check className="w-5 h-5 shrink-0" /> {b}</li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col gap-3">
                <Link to="/boutique" data-testid="espace-shop-btn" className="rounded-full bg-background text-foreground px-9 py-4 text-center hover:scale-105 transition-transform duration-300">Faire un premier achat</Link>
                <a href={APP_URL} target="_blank" rel="noopener noreferrer" className="rounded-full border border-warm-foreground/40 px-9 py-4 text-center hover:bg-warm-foreground/10 transition-colors" data-testid="espace-discover-btn">Découvrir Passerelle</a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* UNIVERS DE L'APPLICATION & LEURS GARDIENS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20" data-testid="app-univers">
        <Reveal>
          <h2 className="font-serif-display italic text-4xl sm:text-5xl font-light mb-4 max-w-2xl">Chaque univers de l'application a son Gardien</h2>
          <p className="text-foreground/70 max-w-2xl leading-relaxed mb-12">Dix expériences pensées pour les besoins de chacun, veillées par les Gardiens des Souvenirs et des Forces Intérieures.</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {APP_UNIVERS.map((u, i) => (
            <Reveal key={u.label} delay={(i % 3) * 0.08}>
              <div className="bg-card rounded-3xl border border-warm/25 p-6 h-full hover:-translate-y-1 hover:shadow-[0_16px_35px_rgb(0,0,0,0.08)] transition-all duration-500" data-testid={`univers-${u.label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-warm mb-4">{u.label}</p>
                <div className="space-y-4">
                  {u.gardienIds.map((gid) => {
                    const g = gardienById(gid);
                    return (
                      <div key={gid} className="flex items-center gap-4">
                        <img src={g.image} alt={g.name} className="w-14 h-14 rounded-full object-cover object-top border-2 shrink-0" style={{ borderColor: `${g.color}55` }} />
                        <div>
                          <p className="font-serif-display text-lg leading-tight">{g.name}</p>
                          <p className="text-xs uppercase tracking-[0.15em]" style={{ color: g.color }}>{g.value}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="font-serif-display italic text-sm text-foreground/70 mt-4 leading-snug">« {gardienById(u.gardienIds[0]).quote} »</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-accent/30 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><h2 className="font-serif-display text-4xl sm:text-5xl font-light mb-14 max-w-2xl">{c.jeux_features_title}</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <div className="bg-card rounded-3xl border border-border/50 p-10 h-full hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-all duration-500">
                  <f.icon className="w-8 h-8 text-secondary" strokeWidth={1.5} />
                  <h3 className="font-serif-display text-2xl mt-6">{f.t}</h3>
                  <p className="mt-4 text-foreground/70 leading-relaxed">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="text-center mt-16">
              <a href={APP_URL} target="_blank" rel="noopener noreferrer" data-testid="espace-cta-btn" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-9 py-4 hover:scale-105 transition-transform duration-300">
                Découvrir Passerelle <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default EspaceJeux;
